import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, Menu } from 'antd';
import { CustomerServiceOutlined, PhoneOutlined, SoundOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useAgentMapStore } from 'hooks';
// Fix imports using relative paths
import { resetData } from 'store/features/agent-map/agent-map.slice';
import { ChatIcon, PhoneFilledIcon } from 'assets';
import { socketService } from 'utils/agent-map';

// Define types for props and seat data
interface OnlineAgentMapProps {
  singleAgentMapAgentStatuses?: any[];
  showCounters?: boolean;
}

interface Seat {
  agentMapSeatId?: number;
  statusName?: string;
  extensionId?: string;
  shortName?: string;
  fillColor?: string;
  textColor?: string;
  iconColor?: string;
  canCall?: boolean;
  canChat?: boolean;
}

const ALERT_THRESHOLD_SECONDS = 300; // 5 minutes

// formatTime utility function
const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// Helper function to check if seat is logged off
const isLoggedOff = (statusName: string | undefined, extensionId: string | undefined): boolean => {
  return statusName === 'LOGGEDOFF' || statusName === 'UNKNOWN' || !extensionId;
};

/**
 * Component hiển thị bản đồ agent online
 */
const OnlineAgentMap: React.FC<OnlineAgentMapProps> = ({ singleAgentMapAgentStatuses = [], showCounters = false }) => {
  const dispatch = useDispatch();
  const [tick, setTick] = useState(0);

  // State for context menu
  const [menuVisibleSeats, setMenuVisibleSeats] = useState<Record<string, boolean>>({});

  // Use ref to store frozen timer values
  const frozenTimerValues = useRef<Record<string, string>>({});

  // Get agent map data from Redux store
  const { mapData: multipleAgentMaps, getSeatCounters, getSeatSeconds } = useAgentMapStore();

  // Find the online map data (with special ID -999)
  const onlineMap = useMemo(() => {
    return multipleAgentMaps.find((map: any) => map.mapId === -999);
  }, [multipleAgentMaps]);

  // Handle right-click context menu
  const handleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, seat: Seat) => {
      if (seat.statusName === 'ONCALL') {
        e.preventDefault();
        e.stopPropagation();

        // Get current timer value for this seat
        const seatIdKey = (seat.agentMapSeatId || 0).toString();
        const seconds = getSeatSeconds(seat.agentMapSeatId || 0);

        // Store formatted time in ref
        frozenTimerValues.current[seatIdKey] = formatTime(seconds);

        // Set menu visible for this seat
        setMenuVisibleSeats((prev) => ({
          ...prev,
          [seatIdKey]: true
        }));
      }
    },
    [getSeatSeconds]
  );

  // Handle menu visibility change
  const handleMenuVisibleChange = useCallback((visible: boolean, seatId: string) => {
    setMenuVisibleSeats((prev) => ({
      ...prev,
      [seatId]: visible
    }));
  }, []);

  // Handle menu item click
  const handleMenuItemClick = useCallback((action: string, extensionId: string, seatId: string) => {
    // console.log(`${action} action for extension ${extensionId}`);

    // Example monitoring actions
    switch (action) {
      case 'listen':
        // console.log(`Starting silent monitoring for extension ${extensionId}`);
        break;
      case 'barge':
        // console.log(`Starting three-way call for extension ${extensionId}`);
        break;
      case 'whisper':
        // console.log(`Starting whisper mode for extension ${extensionId}`);
        break;
    }

    // Close menu after action
    setTimeout(() => {
      setMenuVisibleSeats((prev) => ({
        ...prev,
        [seatId]: false
      }));
    }, 300);
  }, []);

  // Socket initialization and cleanup
  useEffect(() => {
    socketService.reinitialize();
    return () => {
      dispatch(resetData());
    };
  }, [dispatch]);

  // Timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setTick((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // No longer group seats by status, just get valid online seats directly
  const validOnlineSeats = useMemo(() => {
    if (!onlineMap || !onlineMap.seats) return [];

    // Only return seats that are not LOGGEDOFF and have extensionId
    return onlineMap.seats.filter((seat: Seat) => seat && !isLoggedOff(seat.statusName, seat.extensionId));
  }, [onlineMap]);

  // If no online map data
  if (!onlineMap || !onlineMap.seats || onlineMap.seats.length === 0) {
    return (
      <div className='flex h-full items-center justify-center'>
        <p>Không có dữ liệu bản đồ online</p>
      </div>
    );
  }

  // Define default colors
  const defaultColors = {
    backgroundColor: '#EBEBED',
    textColor: '#000000',
    iconColor: '#000000',
    borderColor: '#B2B9C4'
  };

  // Get colors based on status
  const getStatusColors = (statusName: string | undefined) => {
    switch (statusName) {
      case 'UNPAUSED':
        return { backgroundColor: '#06A77D', textColor: '#FFFFFF', iconColor: '#FFFFFF', borderColor: '#B2B9C4' };
      case 'PAUSED':
        return { backgroundColor: '#FF9500', textColor: '#FFFFFF', iconColor: '#FFFFFF', borderColor: '#B2B9C4' };
      case 'ONCALL':
        return { backgroundColor: '#D1131D', textColor: '#FFFFFF', iconColor: '#FFFFFF', borderColor: '#B2B9C4' };
      case 'RINGING':
        return { backgroundColor: '#AF52DE', textColor: '#FFFFFF', iconColor: '#FFFFFF', borderColor: '#B2B9C4' };
      case 'CREATING_TICKET':
        return { backgroundColor: '#1564E8', textColor: '#FFFFFF', iconColor: '#FFFFFF', borderColor: '#B2B9C4' };
      case 'LOGIN':
      case 'FAILED_LOGIN':
        return { backgroundColor: '#E8F4FC', textColor: '#000000', iconColor: '#000000', borderColor: '#B2B9C4' };
      case 'LOGGEDOFF':
      case 'UNKNOWN':
      default:
        return defaultColors;
    }
  };

  return (
    <div className='flex h-full flex-col'>
      <div className='p-4 text-left'>
        {' '}
        {/* Changed text-center to text-right */}
        <h2 className='text-lg font-bold'>Online</h2>
      </div>

      <div className='flex-1 overflow-auto p-4'>
        <div className='flex flex-wrap justify-start'>
          {' '}
          {/* Changed justify-center to justify-end */}
          {/* Display all seats in a single container */}
          <div className='flex flex-wrap justify-start gap-4'>
            {' '}
            {/* Also changed justify-center to justify-end here for consistency */}
            {validOnlineSeats.map((seat: Seat) => {
              const agentMapSeatId = seat.agentMapSeatId || 0;
              const seatIdKey = agentMapSeatId.toString();
              const seconds = getSeatSeconds(agentMapSeatId);
              const counters = getSeatCounters(agentMapSeatId);
              const showAlert = seconds >= ALERT_THRESHOLD_SECONDS;
              const isOnCall = seat.statusName === 'ONCALL';
              const isMenuVisible = !!menuVisibleSeats[seatIdKey];

              // Get status-based colors
              const agentStatus = singleAgentMapAgentStatuses?.find((status) => status.extension === seat.extensionId);

              // Get colors based on status
              const statusColors = getStatusColors(seat.statusName);

              // Final colors
              const backgroundColor = seat.fillColor || agentStatus?.backgroundColor || statusColors.backgroundColor;
              const textColor = seat.textColor || agentStatus?.textColor || statusColors.textColor;
              const iconColor = seat.iconColor || agentStatus?.iconColor || statusColors.iconColor;
              const borderColor = statusColors.borderColor;

              // Get timer display (frozen when menu is visible)
              const timerDisplay = isMenuVisible
                ? frozenTimerValues.current[seatIdKey] || formatTime(seconds)
                : formatTime(seconds);

              // Menu items for context menu
              const menu = (
                <Menu onClick={({ key }) => handleMenuItemClick(key as string, seat.extensionId || '', seatIdKey)}>
                  <Menu.Item key='listen' icon={<CustomerServiceOutlined />}>
                    Nghe lén
                  </Menu.Item>
                  <Menu.Item key='barge' icon={<PhoneOutlined />}>
                    Đàm thoại tay ba
                  </Menu.Item>
                  <Menu.Item key='whisper' icon={<SoundOutlined />}>
                    Nghe xen
                  </Menu.Item>
                </Menu>
              );

              // Seat content
              const seatContent = (
                <div
                  className='relative flex h-[88px] w-[88px] flex-col rounded-md border pl-2 pt-2 shadow-md'
                  style={{
                    backgroundColor,
                    color: textColor,
                    borderColor,
                    transition: 'background-color 0.2s ease-in-out',
                    cursor: isOnCall ? 'context-menu' : 'default'
                  }}
                  onContextMenu={(e) => handleContextMenu(e, seat)}
                >
                  {/* Alert indicator */}
                  {showAlert && (
                    <div
                      className='absolute right-0 top-0 h-4 w-4 rounded-full bg-red-600'
                      style={{
                        boxShadow: '0 0 3px rgba(0,0,0,0.3)',
                        border: '1.5px solid white',
                        zIndex: 10
                      }}
                    />
                  )}

                  {!showCounters ? (
                    // Standard layout with frozen timer
                    <>
                      <div className='flex text-xs'>
                        {seat.canCall !== false && (
                          <div className='flex pb-1 pr-1'>
                            <PhoneFilledIcon height={16} width={16} style={{ fill: iconColor }} />
                          </div>
                        )}
                        {seat.canChat !== false && (
                          <div className='flex items-center'>
                            <ChatIcon height={16} width={16} fill={iconColor} />
                          </div>
                        )}
                      </div>
                      <div className='flex flex-col'>
                        <p className='text-xs'>Ext.{seat.extensionId}</p>
                        <p className='text-xs'>{seat.shortName}</p>
                        {seat.extensionId && <p className='text-xs'>{timerDisplay}</p>}
                      </div>
                    </>
                  ) : (
                    // Counter layout
                    <>
                      <div className='flex flex-col'>
                        <p className='text-xs'>Ext.{seat.extensionId}</p>
                        <p className='text-xs'>{seat.shortName}</p>
                      </div>
                      <div className='mb-2 mt-auto flex flex-col gap-1'>
                        {seat.canCall !== false && (
                          <div className='flex items-center'>
                            <PhoneFilledIcon height={16} width={16} style={{ fill: iconColor, marginRight: 2 }} />
                            <span className='text-xs'>{counters.call}</span>
                          </div>
                        )}
                        {seat.canChat !== false && (
                          <div className='flex items-center'>
                            <ChatIcon height={16} width={16} fill={iconColor} style={{ marginRight: 2 }} />
                            <span className='text-xs'>{counters.chat}</span>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );

              // Wrap ONCALL seats with dropdown
              return isOnCall ? (
                <Dropdown
                  key={`seat-${agentMapSeatId}-${seat.statusName}`}
                  overlay={menu}
                  trigger={['contextMenu']}
                  visible={isMenuVisible}
                  onVisibleChange={(visible) => handleMenuVisibleChange(visible, seatIdKey)}
                  destroyPopupOnHide={false}
                >
                  {seatContent}
                </Dropdown>
              ) : (
                <div key={`seat-${agentMapSeatId}-${seat.statusName}`}>{seatContent}</div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnlineAgentMap;
